import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Play, StopCircle } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";

import { isModifierKeyPressed, SHORTCUTS } from "@/utils/shortcuts";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";

interface RunButtonProps {
  run: () => void;
  cancel?: () => void;
  isRunning?: boolean;
  disabled?: boolean;
  allowRunWhileRunning?: boolean;

  disabledTooltip?: string;

  buttonText?: {
    idle?: string;
    running?: string;
  };
  tooltipText?: {
    idle?: string;
    running?: string;
    disabled?: string;
  };
  tooltipSide?: "top" | "bottom" | "left" | "right";
  compact?: boolean;
}

const DEFAULT_TEXTS = {
  button: {
    idle: "Run",
    running: "Running...",
  },
  tooltip: {
    idle: "Run the request",
    running: "Request in progress...",
    disabled: "Run button is disabled",
  },
};

const getButtonAnimation = (isRunning: boolean, isAnimating: boolean) => {
  if (isRunning) {
    return {
      scale: [1, 1.02, 1],
      transition: {
        repeat: Infinity,
        duration: 1.2,
        ease: "easeInOut",
      },
    };
  }

  if (isAnimating) {
    return {
      scale: [1, 1.03, 1],
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    };
  }

  return {};
};

const RunButton = ({
  run,
  cancel,
  isRunning,
  disabled,
  disabledTooltip = "Run button is disabled",
  buttonText = DEFAULT_TEXTS.button,
  tooltipText = DEFAULT_TEXTS.tooltip,
  tooltipSide = "top",
  compact = false,
  allowRunWhileRunning = false,
}: RunButtonProps) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const canRun = !disabled && (!isRunning || allowRunWhileRunning);

  const onClick = useCallback(() => {
    if (!canRun) {
      return;
    }
    setIsAnimating(true);
    run();
    setTimeout(() => setIsAnimating(false), 500);
  }, [run, canRun]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isModifierKeyPressed(event) && event.key === "Enter") {
        if (!canRun) {
          return;
        }
        event.preventDefault();
        onClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canRun, onClick]);

  const getTooltipContent = () => {
    if (disabled) {
      return disabledTooltip || tooltipText.disabled;
    }
    if (isRunning && !allowRunWhileRunning) {
      return tooltipText.running;
    }
    return `${isRunning ? tooltipText.running : tooltipText.idle} (${SHORTCUTS.SUBMIT.displayWithPlus})`;
  };

  return (
    <div className="flex gap-2">
      {isRunning && cancel && (
        <BasicTooltip content="Cancel" side={tooltipSide}>
          <Button
            onClick={cancel}
            variant="destructiveOutline"
            className="h-8 gap-1.5 rounded-md px-2.5 text-xs font-medium transition-all duration-200 ease-in-out hover:brightness-105"
          >
            <StopCircle className="h-3.5 w-3.5 opacity-75" />
            <span className="whitespace-nowrap leading-none">Cancel</span>
          </Button>
        </BasicTooltip>
      )}

      <BasicTooltip content={getTooltipContent()} side={tooltipSide}>
        <div className={`pointer-events-auto z-10 ${disabled ? "opacity-70" : ""}`}>
          <AnimatePresence>
            <motion.div
              initial={{ scale: 1 }}
              animate={getButtonAnimation(isRunning || false, isAnimating)}
              whileTap={canRun ? { scale: 0.98 } : undefined}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 15,
              }}
            >
              <Button
                onClick={onClick}
                disabled={!canRun}
                className={`rounded-md border border-blue-200 bg-blue-50 text-xs font-medium text-blue-700 shadow-sm transition-all duration-200 ease-in-out hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800 ${
                  compact ? "h-7 min-w-0 gap-1.5 px-2" : "h-8 min-w-[120px] gap-1.5 px-2.5"
                } ${canRun ? "hover:brightness-105" : "cursor-not-allowed"}`}
              >
                {isRunning ? (
                  <Loader2
                    className={`${compact ? "h-2.5 w-2.5" : "h-3 w-3"} animate-spin opacity-70`}
                  />
                ) : (
                  <Play className={`${compact ? "h-2.5 w-2.5" : "h-3 w-3"} opacity-80`} />
                )}
                <span className="whitespace-nowrap leading-none">
                  {isRunning ? buttonText.running : buttonText.idle}
                </span>
                {canRun && (!isRunning || allowRunWhileRunning) && (
                  <kbd
                    className={`ml-0.5 select-none rounded border border-blue-200 bg-white/70 font-mono font-medium leading-none text-blue-600 opacity-80 transition-opacity duration-200 ease-in-out ${
                      compact ? "px-1 py-px text-[10px]" : "px-1.5 py-px text-[11px]"
                    }`}
                  >
                    {SHORTCUTS.SUBMIT.display}
                  </kbd>
                )}
              </Button>
            </motion.div>
          </AnimatePresence>
        </div>
      </BasicTooltip>
    </div>
  );
};

export default observer(RunButton);
